<template>
  <CHeader with-subheader>
    <CToggler
      in-header
      class="ml-3 d-lg-none"
      @click="$store.commit('toggleSidebarMobile')"
    />
    <CHeaderBrand
      class="mx-auto d-lg-none"
    >
      <CImg
        width="140"
        height="35"
        :src="imageSwitch"
        alt="Ringba X"
      />
    </CHeaderBrand>
    <CHeaderNav class="d-md-down-none mr-auto">
      <CHeaderNavItem class="px-3">
        <SystemStatus />
      </CHeaderNavItem>
    </CHeaderNav>

    <CHeaderNav>
      <CHeaderNavItem class="px-3">
        <CDropdown 
          v-if="isAccountManager"
          variant="btn-group" 
          color="info"
          toggler-text="My Accounts"
          direction="dropend"
          size="sm"
          dark
        >
          <template #toggler-content>
            <CIcon 
              name="cil-people" 
              class="mr-2 mt-1" 
            />My Accounts
          </template>
          <CDropdownItem
            class="d-flex align-items-center"
            @click="$router.push(`/dashboard/accounts/buyer?accountManager=${currentAccountManagerId}`)"
          >
            Buyers
          </CDropdownItem>
          <CDropdownItem
            class="d-flex align-items-center"
            @click="$router.push(`/dashboard/accounts/seller?accountManager=${currentAccountManagerId}`)"
          >
            Sellers
          </CDropdownItem>
        </CDropdown>
      </CHeaderNavItem>
    </CHeaderNav>

    <CHeaderNav>
      <CHeaderNavItem class="px-3">
        <CDropdown 
          v-if="isAccountManager"
          variant="btn-group" 
          color="info"
          toggler-text="Copy Signup Link"
          direction="dropend"
          size="sm"
          dark
        >
          <template #toggler-content>
            <CIcon 
              name="cil-copy" 
              class="mr-2 mt-1" 
            />Copy Signup Link
          </template>
          <CDropdownItem
            class="d-flex align-items-center"
            @click="copySignupLinkToClipboard(currentAccountManagerId, 'buyer-registration-apply')"
          >
            Buyer
          </CDropdownItem>
          <CDropdownItem
            class="d-flex align-items-center"
            @click="copySignupLinkToClipboard(currentAccountManagerId, 'seller-registration-apply')"
          >
            Seller
          </CDropdownItem>
        </CDropdown>
      </CHeaderNavItem>
    </CHeaderNav>

    <CHeaderNav>
      <CHeaderNavItem class="px-3">
        <button
          class="ai-header-btn"
          title="Ask RingbaX AI (Ctrl+I)"
          @click="toggleAiPanel"
        >
          <span class="ai-header-btn__sparkle">✦</span>
          <span class="ai-header-btn__label">Ask AI</span>
          <span class="ai-header-btn__kbd">Ctrl+I</span>
        </button>
      </CHeaderNavItem>

      <CHeaderNavItem class="px-3">
        <button
          class="c-header-nav-btn"
          @click="toggleDarkMode"
        >
          <CIcon
            v-if="darkMode"
            name="cil-sun"
          />
          <CIcon
            v-else
            name="cil-moon"
          />
        </button>
      </CHeaderNavItem>

      <TheHeaderDropdownAccnt class="mr-4" />

      <CHeaderNavItem
        v-if="false"
        class="px-3"
      >
        <button
          class="c-header-nav-btn"
          @click="$store.commit('toggle', 'asideShow')"
        >
          <CIcon
            size="lg"
            name="cil-applications-settings"
            class="mr-2"
          />
        </button>
      </CHeaderNavItem>
    </CHeaderNav>
  </CHeader>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import TheHeaderDropdownAccnt from './TheHeaderDropdownAccnt.vue'
import SystemStatus from '@/components/SystemStatus.vue'
import { showSuccessMessage } from '@/notification-utils'

export default {
  name: 'TheHeader',

  components: {
    TheHeaderDropdownAccnt,
    SystemStatus
  },

  mixins: [copyToClipboardMixin],

  computed: {
    ...mapState(['darkMode']),

    ...mapState('auth', ['userDetails']),

    ...mapGetters('auth', [
      'currentAccountManagerId',
      'isAccountManager'
    ]),

    imageSwitch() {
      return this.darkMode ? '/img/ringbax-logo-dark-theme.svg' : '/img/ringbax-logo-light-theme.svg'
    }
  },

  watch: {
    userDetails: function(newValue) {
      if (newValue?.adminSitePreferences?.preferredTheme) {
        const darkMode = newValue.adminSitePreferences.preferredTheme === 'Dark'
        this.set(['darkMode', darkMode])
      }
    }
  },

  methods: {
    ...mapMutations(['set']),

    ...mapActions('user', ['setUIPreferences']),

    ...mapActions('auth', ['getLoggedInUserDetails']),

    ...mapActions('aiAgent', { toggleAiPanel: 'togglePanel' }),

    copySignupLinkToClipboard(id, route) {
      const link = `${this.CLIENT_SIDE_BASE_URL}/${route}?accountManagerId=${id}`

      this.copyTextToClipboard(link)
      showSuccessMessage('Signup link copied to clipboard')
    },

    async toggleDarkMode() {
      const preferences = {
        preferredTheme: this.darkMode ? 'Light' : 'Dark'
      }
      
      await this.setUIPreferences(preferences)

      await this.getLoggedInUserDetails()
    },
  }
}
</script>

<style lang="scss" scoped>
.ai-header-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px 5px 8px;
  background: linear-gradient(135deg, rgba(91, 115, 232, 0.12) 0%, rgba(139, 92, 246, 0.12) 100%);
  border: 1px solid rgba(91, 115, 232, 0.25);
  border-radius: 999px;
  color: #3858e7;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(91, 115, 232, 0.22) 0%, rgba(139, 92, 246, 0.22) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 115, 232, 0.18);
  }

  &__sparkle {
    color: #8b5cf6;
    font-size: 0.95rem;
    line-height: 1;
    animation: ai-header-sparkle 2.4s ease-in-out infinite;
  }

  &__label { line-height: 1; }

  &__kbd {
    font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
    font-size: 0.65rem;
    color: #5a6782;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(91, 115, 232, 0.2);
    border-radius: 4px;
    padding: 1px 5px;
    line-height: 1;
  }
}

@keyframes ai-header-sparkle {
  0%, 100% { transform: rotate(0) scale(1); opacity: 1; }
  50% { transform: rotate(180deg) scale(1.15); opacity: 0.75; }
}
</style>

<style lang="scss">
.c-dark-theme .ai-header-btn {
  background: linear-gradient(135deg, rgba(91, 115, 232, 0.18) 0%, rgba(139, 92, 246, 0.18) 100%);
  border-color: rgba(126, 169, 245, 0.3);
  color: #7ea9f5;

  &:hover {
    background: linear-gradient(135deg, rgba(91, 115, 232, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%);
  }

  &__kbd {
    background: rgba(44, 50, 84, 0.7);
    border-color: rgba(126, 169, 245, 0.2);
    color: #adbacf;
  }
}
</style>
